import { useEffect, useState } from 'react';

import { useQueryRetryConfig as useQueryRetryConfigBase } from './useQueryRetryConfig';
import { normalizeError } from './utils';

type Dependencies = {
  useRetryConfig: typeof useQueryRetryConfigBase;
};

/**
 * `utils/withRetry`, but for non-lazy GraphQL queries.
 *
 * Hook that automatically retries a refetch function when an error occurs. Uses
 * exponential backoff based on the retry config from Statsig.
 *
 * @param error - The initial error from the query, if any. If provided, will
 * kick off the retries.
 * @param refetch - (Stable) refetch function to call on retry.
 * @param onFailure - (Stable) callback function to call on each retry failure.
 * @param dependencies - Optional dependency overrides (e.g. for testing).
 */
export function useAutoRetryOnError(
  error: Error | undefined,
  refetch: () => Promise<{ error?: Error }>,
  onFailure: (details: { retryCount: number; isFinalAttempt: boolean; error: Error }) => void,
  { useRetryConfig }: Dependencies = {
    useRetryConfig: useQueryRetryConfigBase,
  },
) {
  const { retries: maxRetries, initialDelayMs, backoffFactor } = useRetryConfig();

  const [retryCount, setRetryCount] = useState(0);
  const [succeeded, setSucceeded] = useState(false);

  useEffect(
    function autoRetryOnError() {
      if (!error || succeeded || retryCount >= maxRetries) {
        return;
      }

      const delay = initialDelayMs * Math.pow(backoffFactor, retryCount);

      const handleFailure = (error: Error) => {
        const newRetryCount = retryCount + 1;
        setRetryCount(newRetryCount);
        onFailure({
          retryCount: newRetryCount,
          isFinalAttempt: newRetryCount >= maxRetries,
          error,
        });
      };

      const timeout = setTimeout(async () => {
        try {
          const result = await refetch();
          if (result.error) {
            handleFailure(result.error);
          } else {
            setSucceeded(true);
          }
        } catch (error) {
          handleFailure(normalizeError(error));
        }
      }, delay);

      return () => clearTimeout(timeout);
    },
    [backoffFactor, error, initialDelayMs, maxRetries, onFailure, retryCount, refetch, succeeded],
  );

  const isRetrying = !!error && !succeeded && retryCount < maxRetries;

  return { retryCount, isRetrying };
}
